import { useEffect, useState } from 'react';
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaLink,
  FaLinkedinIn,
  FaLocationDot,
  FaPhone,
  FaStar,
  FaTelegram,
  FaTiktok,
  FaWhatsapp,
} from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';

import ActionMenu from '../../components/ui/ActionMenu';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import VariantSpecsPills from '../../components/ui/VariantSpecsPills';
import { useAuth } from '../../context/AuthContext';
import SupplierContactsSection from './SupplierContactsSection';
import {
  addProductSupplier,
  createOrUpdateProductSupplier,
  createVariant,
  DEFAULT_PRICING_METHOD,
  deleteVariant,
  deleteProductSupplier,
  getActiveProductVariantsByProductId,
  getCategories,
  getLatestPriceEntry,
  getPricingMethodLabel,
  getProductById,
  getProducts,
  getProductSuppliersBySupplierId,
  getVariantPriceHistory,
  normalizeEditablePricingMethod,
  normalizePricingMethod,
  normalizeVariantNames,
  PRICING_METHOD_OPTIONS,
  setVariantPrice,
  sortPriceHistoryByDateDesc,
  softDeleteSupplier,
  getSupplierById,
  updateVariant,
} from '../../services/firestoreService';
import { buildLookupMap, getDisplayValues } from '../../utils/collectionUtils';
import {
  normalizeDialNumber,
  normalizeWhatsappNumber,
  sortContactMethods,
} from '../../utils/contactMethods';
import { normalizeUrlValue } from './supplierFormUtils';

const socialPlatforms = [
  { key: 'facebook', label: 'Facebook', Icon: FaFacebookF },
  { key: 'instagram', label: 'Instagram', Icon: FaInstagram },
  { key: 'tiktok', label: 'TikTok', Icon: FaTiktok },
  { key: 'linkedin', label: 'LinkedIn', Icon: FaLinkedinIn },
  { key: 'telegram', label: 'Telegram', Icon: FaTelegram },
];

const hasDisplayValue = (value) =>
  typeof value === 'string' ? Boolean(value.trim()) : Boolean(value);

const missingValueLabel = 'Not provided';

const formatPrice = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '-';
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return `$${numericValue.toFixed(2)}`;
  }

  return '-';
};

const formatPriceWithPricingMethod = (formattedPrice, pricingMethod) =>
  `${formattedPrice} / ${getPricingMethodLabel(pricingMethod)}`;

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const formatDate = (value) => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) {
    return '-';
  }

  return parsedDate.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const normalizeHistoryNote = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : '-';

const createEmptySpecRow = () => ({ key: '', value: '' });

const specsToRows = (specs) => {
  const entries = Object.entries(specs || {}).filter(([key, value]) =>
    Boolean(String(key || '').trim()) && Boolean(String(value || '').trim())
  );

  if (entries.length === 0) {
    return [createEmptySpecRow()];
  }

  return entries.map(([key, value]) => ({
    key: String(key),
    value: String(value),
  }));
};

const getProductName = (productId, productMap) =>
  productMap[productId]?.name || 'Unknown product';

const getProductType = (productId, productMap) =>
  ['simple', 'variant', 'mixed'].includes(productMap[productId]?.type)
    ? productMap[productId].type
    : 'simple';

const getVariantName = (variant) =>
  variant?.variantName || variant?.name || 'Unnamed variant';

const getVariantsForProduct = (
  productId,
  variantsByProductId,
  supplierId,
  productSupplierId = ''
) =>
  (Array.isArray(variantsByProductId[productId]) ? variantsByProductId[productId] : [])
    .filter((variant) => {
      if (variant?.supplierId) {
        return variant.supplierId === supplierId;
      }

      if (variant?.productSupplierId) {
        return variant.productSupplierId === productSupplierId;
      }

      return false;
    });

const getAllowedVariantsForProduct = (productId, productMap) =>
  normalizeVariantNames(productMap[productId]?.allowedVariants);

const getVariantNamesForSupplier = (variants = []) =>
  new Set(
    (Array.isArray(variants) ? variants : [])
      .map((variant) => getVariantName(variant))
      .filter(Boolean)
  );

const getAvailableAllowedVariantsForSupplier = (
  productId,
  productMap,
  variantsByProductId,
  supplierId
) => {
  const allowedVariants = getAllowedVariantsForProduct(productId, productMap);
  const supplierVariantNames = getVariantNamesForSupplier(
    getVariantsForProduct(productId, variantsByProductId, supplierId)
  );

  return allowedVariants.filter((variantName) => !supplierVariantNames.has(variantName));
};

const buildProductPricingRow = (productSupplier, productMap) => {
  const latestPriceEntry = getLatestPriceEntry(productSupplier?.priceHistory);
  const productType = getProductType(productSupplier?.productId, productMap);
  const pricingMethod = normalizePricingMethod(productSupplier?.pricingMethod);
  const simplePriceValue = Number(latestPriceEntry?.price);
  const simpleLastUpdateDate = parseDateValue(latestPriceEntry?.date);

  return {
    id: productSupplier?.id || `${productSupplier?.productId || 'unknown'}-product-price`,
    productSupplierId: productSupplier?.id || '',
    productId: productSupplier?.productId || '',
    productName: getProductName(productSupplier?.productId, productMap),
    productType,
    pricingMethod,
    pricingMethodLabel: getPricingMethodLabel(pricingMethod),
    simplePrice:
      productType === 'simple'
        ? formatPriceWithPricingMethod(formatPrice(latestPriceEntry?.price), pricingMethod)
        : '-',
    simplePriceValue:
      productType === 'simple' && Number.isFinite(simplePriceValue)
        ? simplePriceValue
        : null,
    simpleLastUpdate: productType === 'simple' ? latestPriceEntry?.date : null,
    simpleLastUpdatedTime:
      productType === 'simple' && simpleLastUpdateDate
        ? simpleLastUpdateDate.getTime()
        : null,
    simpleNote: productType === 'simple' ? normalizeHistoryNote(latestPriceEntry?.note) : '-',
    simpleHistory: Array.isArray(productSupplier?.priceHistory)
      ? sortPriceHistoryByDateDesc(productSupplier.priceHistory)
      : [],
  };
};

const productPricingSortOptions = [
  { value: 'lowest', label: 'Lowest Price' },
  { value: 'highest', label: 'Highest Price' },
  { value: 'latest', label: 'Latest Update' },
  { value: 'name', label: 'Product Name' },
];

const getProductPricingSortValues = (row = {}, selectedVariantName = '') => {
  if (!row.isVariantProduct) {
    return {
      priceValue: row.simplePriceValue,
      lastUpdatedTime: row.simpleLastUpdatedTime,
    };
  }

  const activeVariant =
    row.variants.find((variant) => getVariantName(variant) === selectedVariantName) ||
    row.variants[0] ||
    null;
  const variantPriceValue = Number(activeVariant?.currentPrice);
  const variantLastUpdate = parseDateValue(activeVariant?.lastUpdated);

  return {
    priceValue: Number.isFinite(variantPriceValue) ? variantPriceValue : null,
    lastUpdatedTime: variantLastUpdate ? variantLastUpdate.getTime() : null,
  };
};

const sortProductPricingRows = (
  rows,
  sortMode,
  selectedVariantByProductId
) => {
  const direction = sortMode === 'highest' ? -1 : 1;

  return [...rows].sort((firstRow, secondRow) => {
    if (sortMode === 'name') {
      return firstRow.productName.localeCompare(secondRow.productName);
    }

    const firstValues = getProductPricingSortValues(
      firstRow,
      selectedVariantByProductId[firstRow.productId]
    );
    const secondValues = getProductPricingSortValues(
      secondRow,
      selectedVariantByProductId[secondRow.productId]
    );

    if (sortMode === 'latest') {
      const firstTime = firstValues.lastUpdatedTime ?? Number.NEGATIVE_INFINITY;
      const secondTime = secondValues.lastUpdatedTime ?? Number.NEGATIVE_INFINITY;

      if (firstTime !== secondTime) {
        return secondTime - firstTime;
      }
    } else {
      const firstHasPrice = firstValues.priceValue !== null;
      const secondHasPrice = secondValues.priceValue !== null;

      if (firstHasPrice !== secondHasPrice) {
        return firstHasPrice ? -1 : 1;
      }

      if (
        firstHasPrice &&
        secondHasPrice &&
        firstValues.priceValue !== secondValues.priceValue
      ) {
        return (firstValues.priceValue - secondValues.priceValue) * direction;
      }
    }

    return firstRow.productName.localeCompare(secondRow.productName);
  });
};

export default function SupplierDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, role } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryMap, setCategoryMap] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [linkedProductSuppliers, setLinkedProductSuppliers] = useState([]);
  const [variantsByProductId, setVariantsByProductId] = useState({});
  const [selectedVariantByProductId, setSelectedVariantByProductId] = useState({});
  const [productPricingSort, setProductPricingSort] = useState('name');
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isManageProductsModalOpen, setIsManageProductsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [variantModalMode, setVariantModalMode] = useState('add');
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedProductSupplier, setSelectedProductSupplier] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [removeTargetType, setRemoveTargetType] = useState('product');
  const [variantName, setVariantName] = useState('');
  const [variantSpecs, setVariantSpecs] = useState([createEmptySpecRow()]);
  const [variantNote, setVariantNote] = useState('');
  const [variantInitialPrice, setVariantInitialPrice] = useState('');
  const [variantInitialPricingMethod, setVariantInitialPricingMethod] =
    useState(DEFAULT_PRICING_METHOD);
  const [price, setPrice] = useState('');
  const [pricingMethod, setPricingMethod] = useState(DEFAULT_PRICING_METHOD);
  const [priceNote, setPriceNote] = useState('');
  const [addProductId, setAddProductId] = useState('');
  const [addProductSearch, setAddProductSearch] = useState('');
  const [addProductError, setAddProductError] = useState('');
  const [supplierPriceError, setSupplierPriceError] = useState('');
  const [isSubmittingPrice, setIsSubmittingPrice] = useState(false);
  const [isAddingProductPrice, setIsAddingProductPrice] = useState(false);
  const [isSavingVariant, setIsSavingVariant] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isRemovingLink, setIsRemovingLink] = useState(false);

  const loadLinkedProducts = async (supplierId) => {
    const nextProductSuppliers = await getProductSuppliersBySupplierId(supplierId);
    const safeProductSuppliers = Array.isArray(nextProductSuppliers) ? nextProductSuppliers : [];
    const productIds = [...new Set(safeProductSuppliers.map((item) => item?.productId).filter(Boolean))];

    if (productIds.length === 0) {
      setLinkedProductSuppliers([]);
      setProductMap({});
      setVariantsByProductId({});
      setSelectedVariantByProductId({});
      return;
    }

    const products = await Promise.all(productIds.map((productId) => getProductById(productId)));
    const nextProductMap = products.reduce((accumulator, product) => {
      if (product?.id) {
        accumulator[product.id] = product;
      }

      return accumulator;
    }, {});
    const variantProductIds = productIds.filter(
      (productId) => nextProductMap[productId]?.type === 'variant'
    );
    const variantsResults = await Promise.all(
      variantProductIds.map(async (productId) => ({
        productId,
        variants: await getActiveProductVariantsByProductId(productId),
      }))
    );

    setLinkedProductSuppliers(safeProductSuppliers);
    setProductMap(nextProductMap);
    setVariantsByProductId(
      variantsResults.reduce((accumulator, item) => {
        accumulator[item.productId] = Array.isArray(item.variants) ? item.variants : [];
        return accumulator;
      }, {})
    );
    setSelectedVariantByProductId((previous) =>
      productIds.reduce((accumulator, productId) => {
        if (previous[productId]) {
          accumulator[productId] = previous[productId];
        }

        return accumulator;
      }, {})
    );
  };

  useEffect(() => {
    const fetchSupplierAndCategories = async () => {
      setLoading(true);
      setPageError('');

      try {
        const [nextSupplier, categories, products] = await Promise.all([
          getSupplierById(id),
          getCategories(),
          getProducts(),
        ]);

        setSupplier(nextSupplier);
        setCategoryMap(buildLookupMap(categories, 'name'));
        setAllProducts(
          (Array.isArray(products) ? products : []).filter(
            (product) =>
              product?.id &&
              product.isDeleted !== true &&
              product.type !== 'mixed'
          )
        );

        if (nextSupplier) {
          await loadLinkedProducts(id);
        } else {
          setLinkedProductSuppliers([]);
          setProductMap({});
          setVariantsByProductId({});
          setSelectedVariantByProductId({});
        }
      } catch (error) {
        console.error('Error fetching supplier details:', error);
        setSupplier(null);
        setCategoryMap({});
        setAllProducts([]);
        setLinkedProductSuppliers([]);
        setProductMap({});
        setVariantsByProductId({});
        setSelectedVariantByProductId({});
        setPageError('Failed to load supplier details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierAndCategories();
  }, [id]);

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);

    try {
      await softDeleteSupplier(id);
      navigate('/suppliers');
    } catch (error) {
      console.error('Delete error:', error);
      setPageError('Failed to delete supplier.');
      setIsDeleting(false);
    }
  };

  const resetPriceForm = () => {
    setPrice('');
    setPricingMethod(DEFAULT_PRICING_METHOD);
    setPriceNote('');
    setSupplierPriceError('');
    setIsSubmittingPrice(false);
  };

  const resetAddProductForm = () => {
    setAddProductId('');
    setAddProductSearch('');
    setAddProductError('');
    setIsAddingProductPrice(false);
  };

  const resetVariantForm = () => {
    setVariantName('');
    setVariantSpecs([createEmptySpecRow()]);
    setVariantNote('');
    setVariantInitialPrice('');
    setVariantInitialPricingMethod(DEFAULT_PRICING_METHOD);
    setSupplierPriceError('');
    setIsSavingVariant(false);
  };

  const refreshVariantsForProduct = async (productId) => {
    if (!productId) {
      return;
    }

    const variants = await getActiveProductVariantsByProductId(productId);
    setVariantsByProductId((previous) => ({
      ...previous,
      [productId]: Array.isArray(variants) ? variants : [],
    }));
  };

  const handleOpenAddProductModal = () => {
    resetAddProductForm();
    setIsAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    if (isAddingProductPrice) {
      return;
    }

    setIsAddProductModalOpen(false);
    resetAddProductForm();
  };

  const handleAddProductChange = (nextProductId) => {
    setAddProductId(nextProductId);
    setAddProductError('');
  };

  const handleOpenAddVariantModal = (productId, productName) => {
    const availableVariants = getAvailableAllowedVariantsForSupplier(
      productId,
      productMap,
      variantsByProductId,
      id
    );

    setVariantModalMode('add');
    setSelectedVariant(null);
    setSelectedProductId(productId || '');
    setSelectedProductName(productName || 'Unknown product');
    resetVariantForm();
    setVariantName(availableVariants[0] || '');
    setIsVariantModalOpen(true);
  };

  const handleOpenEditVariantModal = (variant, productName) => {
    setVariantModalMode('edit');
    setSelectedVariant(variant);
    setSelectedProductId(variant?.productId || '');
    setSelectedProductName(`${productName || 'Unknown product'} / ${getVariantName(variant)}`);
    setVariantName(getVariantName(variant));
    setVariantSpecs(specsToRows(variant?.specs));
    setVariantNote(variant?.notes || '');
    setVariantInitialPrice('');
    setSupplierPriceError('');
    setIsSavingVariant(false);
    setIsVariantModalOpen(true);
  };

  const handleCloseVariantModal = () => {
    if (isSavingVariant) {
      return;
    }

    setIsVariantModalOpen(false);
    setVariantModalMode('add');
    setSelectedVariant(null);
    setSelectedProductId('');
    setSelectedProductName('');
    resetVariantForm();
  };

  const handleOpenAddPriceModal = (variant, productName) => {
    setSelectedVariant(variant);
    setSelectedProductSupplier(null);
    setSelectedProductId(variant?.productId || '');
    setSelectedProductName(`${productName || 'Unknown product'} / ${getVariantName(variant)}`);
    resetPriceForm();
    setPricingMethod(
      normalizeEditablePricingMethod(
        linkedProductSuppliers.find(
          (item) =>
            item?.id === variant?.productSupplierId ||
            item?.productId === variant?.productId
        )?.pricingMethod
      )
    );
    setIsPriceModalOpen(true);
  };

  const handleOpenAddSimplePriceModal = (row) => {
    setSelectedVariant(null);
    setSelectedProductSupplier(row);
    setSelectedProductId(row?.productId || '');
    setSelectedProductName(row?.productName || 'Unknown product');
    resetPriceForm();
    setPricingMethod(normalizeEditablePricingMethod(row?.pricingMethod));
    setIsPriceModalOpen(true);
  };

  const handleClosePriceModal = () => {
    if (isSubmittingPrice) {
      return;
    }

    setIsPriceModalOpen(false);
    setSelectedVariant(null);
    setSelectedProductSupplier(null);
    setSelectedProductId('');
    setSelectedProductName('');
    resetPriceForm();
  };

  const handleOpenHistoryModal = async (variant, productName) => {
    setSelectedProductName(`${productName || 'Unknown product'} / ${getVariantName(variant)}`);
    setSelectedHistory([]);
    setIsHistoryModalOpen(true);
    setIsHistoryLoading(true);

    try {
      const history = await getVariantPriceHistory(variant?.id, variant?.priceHistory);
      setSelectedHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Error loading price history:', error);
      setSelectedHistory([]);
      setPageError('Failed to load price history.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleOpenSimpleHistoryModal = (row) => {
    setSelectedProductName(row?.productName || 'Unknown product');
    setSelectedHistory(Array.isArray(row?.simpleHistory) ? row.simpleHistory : []);
    setIsHistoryModalOpen(true);
    setIsHistoryLoading(false);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedProductName('');
    setSelectedHistory([]);
    setIsHistoryLoading(false);
  };

  const handleOpenRemoveProductLinkModal = (row) => {
    setSelectedProductSupplier(row);
    setSelectedVariant(null);
    setSelectedProductName(row?.productName || 'Unknown product');
    setRemoveTargetType('product');
    setIsRemoveModalOpen(true);
  };

  const handleOpenRemoveVariantModal = (variant, productName) => {
    setSelectedVariant(variant);
    setSelectedProductSupplier(null);
    setSelectedProductId(variant?.productId || '');
    setSelectedProductName(`${productName || 'Unknown product'} / ${getVariantName(variant)}`);
    setRemoveTargetType('variant');
    setIsRemoveModalOpen(true);
  };

  const handleCloseRemoveModal = () => {
    if (isRemovingLink) {
      return;
    }

    setIsRemoveModalOpen(false);
    setSelectedProductSupplier(null);
    setSelectedVariant(null);
    setSelectedProductId('');
    setSelectedProductName('');
    setRemoveTargetType('product');
  };

  const handleSubmitProductPrice = async (event) => {
    event.preventDefault();

    if (!selectedVariant?.id && !selectedProductSupplier?.productId) {
      setSupplierPriceError('Product or variant is required.');
      return;
    }

    if (String(price).trim() === '') {
      setSupplierPriceError('Price is required.');
      return;
    }

    const parsedPrice = Number(price);

    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      setSupplierPriceError('Price must be a positive number.');
      return;
    }

    setIsSubmittingPrice(true);
    setSupplierPriceError('');

    try {
      const newEntry = {
        price: parsedPrice,
        pricingMethod,
        date: new Date(),
        note: priceNote.trim(),
      };

      if (selectedVariant?.id) {
        await addProductSupplier(selectedVariant.productId || selectedProductId, id, {
          pricingMethod,
        });
        await setVariantPrice(selectedVariant.id, newEntry);
        await refreshVariantsForProduct(selectedVariant.productId || selectedProductId);
        await loadLinkedProducts(id);
      } else {
        const updatedProductSupplier = await createOrUpdateProductSupplier(
          selectedProductSupplier.productId,
          id,
          newEntry
        );

        setLinkedProductSuppliers((previous) =>
          previous.map((productSupplier) =>
            productSupplier.id === updatedProductSupplier.id
              ? updatedProductSupplier
              : productSupplier
          )
        );
      }

      setIsPriceModalOpen(false);
      setSelectedVariant(null);
      setSelectedProductSupplier(null);
      setSelectedProductId('');
      setSelectedProductName('');
      setPageMessage(selectedVariant?.id ? 'Variant price saved.' : 'Product price saved.');
      setPageError('');
      resetPriceForm();
    } catch (error) {
      console.error('Error saving variant price:', error);
      setSupplierPriceError('Failed to save variant price.');
      setIsSubmittingPrice(false);
    }
  };

  const handleSubmitAddProduct = async (event) => {
    event.preventDefault();

    if (isAddingProductPrice) {
      return;
    }

    const selectedProduct = allProducts.find((product) => product.id === addProductId);

    if (!selectedProduct?.id) {
      setAddProductError('Product is required.');
      return;
    }

    if (selectedProduct.type === 'mixed') {
      setAddProductError('Mixed products use components instead of supplier pricing.');
      return;
    }

    setIsAddingProductPrice(true);
    setAddProductError('');

    try {
      await addProductSupplier(selectedProduct.id, id, {
        pricingMethod: DEFAULT_PRICING_METHOD,
      });
      await loadLinkedProducts(id);
      setIsAddProductModalOpen(false);
      setPageMessage('Product link added.');
      setPageError('');
      resetAddProductForm();
    } catch (error) {
      console.error('Error adding product link:', error);
      setAddProductError('Failed to add product link.');
      setIsAddingProductPrice(false);
    }
  };

  const handleSubmitVariant = async (event) => {
    event.preventDefault();

    if (!selectedProductId) {
      setSupplierPriceError('Product is required.');
      return;
    }

    if (variantModalMode === 'edit' && !selectedVariant?.id) {
      setSupplierPriceError('Variant is required.');
      return;
    }

    if (!variantName.trim()) {
      setSupplierPriceError('Variant name is required.');
      return;
    }

    const allowedVariants = getAllowedVariantsForProduct(selectedProductId, productMap);
    const selectedVariantName = variantName.trim();
    const isUnchangedLegacyVariant =
      variantModalMode === 'edit' &&
      selectedVariant?.id &&
      selectedVariantName === getVariantName(selectedVariant) &&
      !allowedVariants.includes(selectedVariantName);

    if (allowedVariants.length === 0 && !isUnchangedLegacyVariant) {
      setSupplierPriceError('This product has no allowed variants yet.');
      return;
    }

    if (!allowedVariants.includes(selectedVariantName) && !isUnchangedLegacyVariant) {
      setSupplierPriceError('Select one of the product allowed variants.');
      return;
    }

    if (variantModalMode === 'edit') {
      const existingVariantNames = getVariantNamesForSupplier(
        getVariantsForProduct(selectedProductId, variantsByProductId, id)
      );
      existingVariantNames.delete(getVariantName(selectedVariant));

      if (existingVariantNames.has(selectedVariantName)) {
        setSupplierPriceError('This supplier already has that variant.');
        return;
      }
    }

    if (variantModalMode === 'add') {
      const existingVariantNames = getVariantNamesForSupplier(
        getVariantsForProduct(selectedProductId, variantsByProductId, id)
      );

      if (existingVariantNames.has(selectedVariantName)) {
        setSupplierPriceError('This supplier already has that variant.');
        return;
      }

      if (String(variantInitialPrice).trim() === '') {
        setSupplierPriceError('Price is required.');
        return;
      }

      const parsedInitialPrice = Number(variantInitialPrice);

      if (!Number.isFinite(parsedInitialPrice) || parsedInitialPrice <= 0) {
        setSupplierPriceError('Price must be a positive number.');
        return;
      }
    }

    const completeSpecs = variantSpecs.filter((spec) => {
      const hasKey = Boolean(String(spec.key || '').trim());
      const hasValue = Boolean(String(spec.value || '').trim());

      return hasKey && hasValue;
    });
    const hasIncompleteSpec = variantSpecs.some((spec) => {
      const hasKey = Boolean(String(spec.key || '').trim());
      const hasValue = Boolean(String(spec.value || '').trim());

      return hasKey !== hasValue;
    });

    if (hasIncompleteSpec) {
      setSupplierPriceError('Specs require both key and value.');
      return;
    }

    setIsSavingVariant(true);
    setSupplierPriceError('');

    try {
      const payload = {
        variantName: selectedVariantName,
        specs: completeSpecs,
        notes: variantNote.trim(),
        productId: selectedProductId,
        supplierId: id,
        productSupplierId:
          linkedProductSuppliers.find((item) => item?.productId === selectedProductId)?.id || '',
      };

      if (variantModalMode === 'edit') {
        await updateVariant(selectedVariant.id, payload);
      } else {
        const productSupplier = await addProductSupplier(selectedProductId, id, {
          pricingMethod: variantInitialPricingMethod,
        });
        const createdVariant = await createVariant(selectedProductId, {
          ...payload,
          productSupplierId: productSupplier?.id || payload.productSupplierId,
        });
        await setVariantPrice(createdVariant.id, {
          price: Number(variantInitialPrice),
          pricingMethod: variantInitialPricingMethod,
          date: new Date(),
          note: variantNote.trim(),
        });
      }

      await refreshVariantsForProduct(selectedProductId);
      await loadLinkedProducts(id);
      setIsVariantModalOpen(false);
      setVariantModalMode('add');
      setSelectedVariant(null);
      setSelectedProductId('');
      setSelectedProductName('');
      setPageMessage(variantModalMode === 'edit' ? 'Variant updated.' : 'Variant added.');
      setPageError('');
      resetVariantForm();
    } catch (error) {
      console.error('Error saving variant:', error);
      setSupplierPriceError('Failed to save variant.');
      setIsSavingVariant(false);
    }
  };

  const handleConfirmRemoveLink = async () => {
    if (isRemovingLink) {
      return;
    }

    if (removeTargetType === 'variant' && !selectedVariant?.id) {
      return;
    }

    if (removeTargetType !== 'variant' && !selectedProductSupplier?.productId) {
      return;
    }

    setIsRemovingLink(true);

    try {
      if (removeTargetType === 'variant') {
        await deleteVariant(selectedVariant.id);
        await refreshVariantsForProduct(selectedVariant.productId || selectedProductId);
        setPageMessage('Variant removed.');
      } else {
        await deleteProductSupplier(selectedProductSupplier.productId, id);
        await loadLinkedProducts(id);
        setPageMessage('Product link removed.');
      }

      setIsRemoveModalOpen(false);
      setSelectedProductSupplier(null);
      setSelectedVariant(null);
      setSelectedProductId('');
      setSelectedProductName('');
      setRemoveTargetType('product');
      setPageError('');
      setIsRemovingLink(false);
    } catch (error) {
      console.error('Error removing item:', error);
      setPageError(removeTargetType === 'variant' ? 'Failed to remove variant.' : 'Failed to remove product link.');
      setIsRemovingLink(false);
    }
  };

  if (loading) {
    return <p>Loading supplier...</p>;
  }

  if (!supplier) {
    return (
      <div className="page">
        <StatusMessage tone="error" message={pageError} />
        <p>Supplier not found</p>
      </div>
    );
  }

  const categoryNames = getDisplayValues(supplier.categories, categoryMap);
  const canManageContacts = ['owner', 'admin', 'manager'].includes(role);
  const productPricingRows = sortProductPricingRows(
    linkedProductSuppliers.map((productSupplier) => {
      const row = buildProductPricingRow(productSupplier, productMap);
      const isVariantProduct = row.productType === 'variant';
      const variants = isVariantProduct
        ? getVariantsForProduct(row.productId, variantsByProductId, id, row.productSupplierId)
        : [];
      const allowedVariants = isVariantProduct
        ? getAllowedVariantsForProduct(row.productId, productMap)
        : [];
      const availableAllowedVariants = isVariantProduct
        ? getAvailableAllowedVariantsForSupplier(
          row.productId,
          productMap,
          variantsByProductId,
          id
        )
        : [];

      return {
        ...row,
        isVariantProduct,
        variants,
        allowedVariants,
        availableAllowedVariants,
      };
    }).filter(Boolean),
    productPricingSort,
    selectedVariantByProductId
  );
  const linkedProductIds = new Set(
    productPricingRows.map((row) => row.productId).filter(Boolean)
  );
  const availableProductsToLink = allProducts.filter(
    (product) => product?.id && !linkedProductIds.has(product.id)
  );
  const filteredProductsToLink = availableProductsToLink.filter((product) => {
    const normalizedSearchTerm = addProductSearch.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return true;
    }

    return String(product.name || '')
      .toLowerCase()
      .includes(normalizedSearchTerm);
  });
  const contactMethods = sortContactMethods(supplier.contactMethods);
  const companyInfoItems = [
    {
      label: 'Company Email',
      value: supplier.companyEmail || supplier.email,
      hrefType: 'email',
      Icon: FaEnvelope,
    },
    {
      label: 'Website',
      value: supplier.website,
      hrefType: 'website',
      Icon: FaLink,
    },
    {
      label: 'Address',
      value: supplier.address,
      hrefType: 'address',
      mapsLink: supplier.mapsLink,
    },
    { label: 'Comments', value: supplier.comments },
  ];
  const socialLinks = socialPlatforms
    .map((platform) => ({
      ...platform,
      href: normalizeUrlValue(supplier.socials?.[platform.key]),
    }))
    .filter((platform) => hasDisplayValue(platform.href));
  const selectedProductAllowedVariants = getAllowedVariantsForProduct(
    selectedProductId,
    productMap
  );
  const selectedProductAvailableVariants =
    variantModalMode === 'add'
      ? getAvailableAllowedVariantsForSupplier(
        selectedProductId,
        productMap,
        variantsByProductId,
        id
      )
      : selectedProductAllowedVariants.filter((variantNameOption) => {
        const currentVariantName = selectedVariant ? getVariantName(selectedVariant) : '';
        const supplierVariantNames = getVariantNamesForSupplier(
          getVariantsForProduct(selectedProductId, variantsByProductId, id)
        );

        supplierVariantNames.delete(currentVariantName);

        return !supplierVariantNames.has(variantNameOption);
      });
  const variantSelectOptions =
    variantModalMode === 'edit' &&
    selectedVariant &&
    !selectedProductAvailableVariants.includes(getVariantName(selectedVariant))
      ? [getVariantName(selectedVariant), ...selectedProductAvailableVariants]
      : selectedProductAvailableVariants;

  const titleRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  };

  const rowActionsStyle = {
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 12,
  };

  const categoriesListStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: 0,
    margin: 0,
    listStyle: 'none',
  };

  const tableWrapStyle = {
    overflowX: 'hidden',
    maxWidth: '100%',
  };

  const tableStyle = {
    width: '100%',
    tableLayout: 'fixed',
  };

  const tableHeadCellStyle = {};

  const tableBodyCellStyle = {
    color: '#334155',
    fontSize: 14,
    verticalAlign: 'top',
  };

  const fieldWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const labelStyle = {
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
  };

  const inputBaseStyle = {
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const emptyStateStyle = {
    margin: 0,
    color: '#64748b',
    lineHeight: 1.6,
  };

  const historyModalContentStyle = {
    marginTop: 20,
    maxHeight: '60vh',
    overflowY: 'auto',
  };

  const modalToolbarStyle = {
    display: 'grid',
    gap: 12,
    marginBottom: 16,
  };

  const renderMissingValue = () => (
    <span className="detail-placeholder">{missingValueLabel}</span>
  );

  const getInteractiveHref = (item) => {
    const value = String(item.value || '').trim();

    if (!value) {
      return '';
    }

    switch (item.hrefType) {
      case 'phone':
        return `tel:${normalizeDialNumber(value)}`;
      case 'whatsapp':
        return `https://wa.me/${normalizeWhatsappNumber(value)}`;
      case 'email':
        return `mailto:${value}`;
      case 'website':
        return normalizeUrlValue(value);
      default:
        return '';
    }
  };

  const getInteractiveTitle = (item) => {
    switch (item.hrefType) {
      case 'phone':
        return 'Call';
      case 'whatsapp':
        return 'Open WhatsApp';
      case 'email':
        return 'Send Email';
      case 'website':
        return 'Open Website';
      default:
        return undefined;
    }
  };

  const renderAddressValue = (item) => {
    const mapsLink = normalizeUrlValue(item.mapsLink);

    return (
      <span className="address-detail-value">
        <span>{item.value}</span>
        {mapsLink ? (
          <a
            href={mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="address-map-link"
            aria-label="Open address in Google Maps"
            title="Open address in Google Maps"
          >
            <FaLocationDot aria-hidden="true" />
          </a>
        ) : null}
      </span>
    );
  };

  const renderDetailValue = (item) => {
    if (!hasDisplayValue(item.value)) {
      return renderMissingValue();
    }

    if (item.hrefType === 'address') {
      return renderAddressValue(item);
    }

    const href = getInteractiveHref(item);

    if (href) {
      return (
        <a
          href={href}
          target={['whatsapp', 'website'].includes(item.hrefType) ? '_blank' : undefined}
          rel={['whatsapp', 'website'].includes(item.hrefType) ? 'noopener noreferrer' : undefined}
          className={`interactive-text-link interactive-text-link--${item.hrefType} detail-link`}
          title={getInteractiveTitle(item)}
        >
          {item.Icon ? <item.Icon aria-hidden="true" className="interactive-link-icon" /> : null}
          {item.value}
        </a>
      );
    }

    return item.value;
  };

  const renderContactMethod = (method) => {
    const callHref = `tel:${normalizeDialNumber(method.number)}`;
    const whatsappHref = `https://wa.me/${normalizeWhatsappNumber(method.number)}`;

    return (
      <div
        key={method.id || `${method.label}-${method.number}`}
        className={`contact-method-detail${method.isPrimary ? ' contact-method-detail--primary' : ''}`}
      >
        <div className="contact-method-detail__header">
          <strong className="contact-method-detail__title">
            {method.isPrimary ? <FaStar aria-hidden="true" className="interactive-link-icon" /> : null}
            {method.isPrimary ? ' ' : ''}
            {method.label || 'Other'}
            {method.isPrimary ? <span className="contact-method-detail__primary-text">Primary</span> : null}
          </strong>
        </div>
        <div className="contact-method-detail__number">{method.number}</div>
        <div className="contact-method-detail__actions">
          <a
            href={callHref}
            className="interactive-text-link interactive-text-link--phone detail-link"
            title="Call"
          >
            <FaPhone aria-hidden="true" className="interactive-link-icon" />
            Call
          </a>
          {method.hasWhatsapp ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="interactive-text-link interactive-text-link--whatsapp detail-link"
              title="Open WhatsApp"
            >
              <FaWhatsapp aria-hidden="true" className="interactive-link-icon" />
              WhatsApp
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const handleSpecChange = (index, fieldName, value) => {
    setVariantSpecs((previousSpecs) =>
      previousSpecs.map((spec, specIndex) =>
        specIndex === index ? { ...spec, [fieldName]: value } : spec
      )
    );

    if (supplierPriceError) {
      setSupplierPriceError('');
    }
  };

  const handleAddSpecField = () => {
    setVariantSpecs((previousSpecs) => [...previousSpecs, createEmptySpecRow()]);
  };

  const handleRemoveSpecField = (index) => {
    setVariantSpecs((previousSpecs) => {
      if (previousSpecs.length === 1) {
        return [createEmptySpecRow()];
      }

      return previousSpecs.filter((_, specIndex) => specIndex !== index);
    });

    if (supplierPriceError) {
      setSupplierPriceError('');
    }
  };

  return (
    <div className="page">
      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <div className="page-header" style={titleRowStyle}>
        <h1 className="page-title">{supplier.companyName}</h1>

        {isAdmin ? (
          <div className="page-header__actions" style={rowActionsStyle}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/supplier/${id}/edit`)}
            >
              Edit
            </button>

            <button
              type="button"
              onClick={() => setIsDeleteModalOpen(true)}
              className="btn btn-danger"
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>

      <section className="surface-card surface-card--section contact-methods-card">
        <div className="company-info-card__header">
          <h2 className="section-title">Contact Methods</h2>
        </div>
        <div className="contact-methods-detail-list">
          {contactMethods.length > 0
            ? contactMethods.map((method) => renderContactMethod(method))
            : renderMissingValue()}
        </div>
      </section>

      <section className="surface-card surface-card--section company-info-card">
        <div className="company-info-card__header">
          <h2 className="section-title">Company Info</h2>
          {socialLinks.length > 0 ? (
            <div className="company-social-icons" aria-label="Social media links">
              {socialLinks.map(({ key, label, href, Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className={`social-icon-link social-icon-link--compact social-icon-link--${key}`}
                  aria-label={label}
                  title={label}
                >
                  <Icon aria-hidden="true" />
                </a>
              ))}
            </div>
          ) : null}
        </div>
        <dl className="detail-grid">
          {companyInfoItems.map((item) => (
            <div
              key={item.label}
              className="detail-item"
            >
              <div className="detail-field-copy">
                <dt>{item.label}</dt>
                <dd>{renderDetailValue(item)}</dd>
              </div>
            </div>
          ))}
        </dl>
      </section>

      <SupplierContactsSection
        supplierId={id}
        canManage={canManageContacts}
      />

      <section className="surface-card surface-card--section">
        <h2 className="section-title">Categories</h2>
        {categoryNames.length > 0 ? (
          <ul style={categoriesListStyle}>
            {categoryNames.map((categoryName) => (
              <li key={categoryName}>
                <span className="tag">{categoryName}</span>
              </li>
            ))}
          </ul>
        ) : (
          renderMissingValue()
        )}
      </section>

      <section className="surface-card surface-card--section">
        <div className="section-header" style={sectionHeaderStyle}>
          <h2 className="section-title">Products & Pricing</h2>
          {isAdmin ? (
            <div className="section-actions" style={rowActionsStyle}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenAddProductModal}
                disabled={availableProductsToLink.length === 0}
              >
                + Add Product
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsManageProductsModalOpen(true)}
                disabled={productPricingRows.length === 0}
              >
                Manage Products
              </button>
            </div>
          ) : null}
        </div>

        {productPricingRows.length === 0 ? (
          <p style={emptyStateStyle}>No linked products for this supplier.</p>
        ) : (
          <>
            <div style={{ maxWidth: 260, marginBottom: 16 }}>
              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Sort</span>
                <select
                  name="product-pricing-sort"
                  value={productPricingSort}
                  onChange={(event) => setProductPricingSort(event.target.value)}
                  style={inputBaseStyle}
                >
                  {productPricingSortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="table-container supplier-detail-table-container" style={tableWrapStyle}>
              <table className="data-table supplier-pricing-table" style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeadCellStyle}>Product</th>
                  <th style={tableHeadCellStyle}>Type</th>
                  <th style={tableHeadCellStyle}>Variant</th>
                  <th style={tableHeadCellStyle}>Specs</th>
                  <th style={tableHeadCellStyle}>Price</th>
                  <th style={tableHeadCellStyle}>Last Update</th>
                  <th style={tableHeadCellStyle}>Notes</th>
                  <th style={tableHeadCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {productPricingRows.map((row) => {
                  const selectedVariantName = selectedVariantByProductId[row.productId];
                  const activeVariant =
                    row.variants.find((variant) => getVariantName(variant) === selectedVariantName) ||
                    row.variants[0] ||
                    null;
                  const activeVariantLatestEntry = getLatestPriceEntry(activeVariant?.priceHistory);
                  const activeVariantNote = activeVariant
                    ? normalizeHistoryNote(activeVariant.notes || activeVariantLatestEntry?.note)
                    : '-';
                  const actionItems = row.isVariantProduct
                    ? [
                      isAdmin && row.availableAllowedVariants.length > 0
                        ? {
                          key: 'add-variant',
                          label: 'Add Variant',
                          onSelect: () => handleOpenAddVariantModal(row.productId, row.productName),
                        }
                        : null,
                      isAdmin && activeVariant
                        ? {
                          key: 'edit-variant',
                          label: 'Edit Variant',
                          onSelect: () => handleOpenEditVariantModal(activeVariant, row.productName),
                        }
                        : null,
                      isAdmin && activeVariant
                        ? {
                          key: 'add-price',
                          label: 'Manage Pricing',
                          onSelect: () => handleOpenAddPriceModal(activeVariant, row.productName),
                        }
                        : null,
                      activeVariant
                        ? {
                          key: 'view-history',
                          label: 'View History',
                          onSelect: () => handleOpenHistoryModal(activeVariant, row.productName),
                        }
                        : null,
                      isAdmin && activeVariant
                        ? {
                          key: 'remove-variant',
                          label: 'Remove Variant',
                          onSelect: () => handleOpenRemoveVariantModal(activeVariant, row.productName),
                          tone: 'danger',
                          disabled: isRemovingLink,
                        }
                        : null,
                      isAdmin
                        ? {
                          key: 'remove-link',
                          label: 'Remove Product Link',
                          onSelect: () => handleOpenRemoveProductLinkModal(row),
                          tone: 'danger',
                          disabled: isRemovingLink,
                        }
                        : null,
                    ].filter(Boolean)
                    : [
                      isAdmin
                        ? {
                          key: 'add-price',
                          label: 'Manage Pricing',
                          onSelect: () => handleOpenAddSimplePriceModal(row),
                        }
                        : null,
                      {
                        key: 'view-history',
                        label: 'View History',
                        onSelect: () => handleOpenSimpleHistoryModal(row),
                      },
                      isAdmin
                        ? {
                          key: 'remove-link',
                          label: 'Remove Product Link',
                          onSelect: () => handleOpenRemoveProductLinkModal(row),
                          tone: 'danger',
                          disabled: isRemovingLink,
                        }
                        : null,
                    ].filter(Boolean);

                  return (
                    <tr key={row.id} className="product-row">
                        <td className="clickable-cell" style={tableBodyCellStyle}>
                          <button
                            type="button"
                            className="clickable-name"
                            onClick={() => navigate(`/products/${row.productId}`)}
                          >
                            {row.productName}
                          </button>
                        </td>
                        <td style={tableBodyCellStyle}>
                          <span className="tag">
                            {row.productType === 'mixed'
                              ? 'Mixed'
                              : row.isVariantProduct
                                ? 'Has Variants'
                                : 'Simple'}
                          </span>
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct ? (
                            row.variants.length > 0 ? (
                              <select
                                value={activeVariant ? getVariantName(activeVariant) : ''}
                                onChange={(event) =>
                                  setSelectedVariantByProductId((previous) => ({
                                    ...previous,
                                    [row.productId]: event.target.value,
                                  }))
                                }
                                style={{ ...inputBaseStyle, minWidth: 112, padding: '8px 10px' }}
                                aria-label={`Select variant for ${row.productName}`}
                              >
                                {row.variants.map((variant) => {
                                  const optionName = getVariantName(variant);

                                  return (
                                    <option key={variant.id || optionName} value={optionName}>
                                      {optionName}
                                    </option>
                                  );
                                })}
                              </select>
                            ) : (
                              '-'
                            )
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct ? (
                            <VariantSpecsPills specs={activeVariant?.specs} />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct
                            ? formatPriceWithPricingMethod(
                              formatPrice(activeVariant?.currentPrice),
                              row.pricingMethod
                            )
                            : row.simplePrice}
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct
                            ? formatDate(activeVariant?.lastUpdated)
                            : formatDate(row.simpleLastUpdate)}
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct ? activeVariantNote : row.simpleNote}
                        </td>
                        <td style={tableBodyCellStyle}>
                          <ActionMenu items={actionItems} />
                        </td>
                      </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {isAdmin ? (
        <FormModal
          isOpen={isAddProductModalOpen}
          onClose={handleCloseAddProductModal}
          onSubmit={handleSubmitAddProduct}
          title="+ Add Product"
          description="Create a new product link for this supplier."
          submitLabel={isAddingProductPrice ? 'Adding...' : 'Add Product'}
          submitDisabled={isAddingProductPrice || filteredProductsToLink.length === 0}
          cancelDisabled={isAddingProductPrice}
          errorMessage={addProductError}
        >
          {availableProductsToLink.length === 0 ? (
            <p style={emptyStateStyle}>All active products are already linked to this supplier.</p>
          ) : (
            <>
              <div style={modalToolbarStyle}>
                <SearchBar
                  value={addProductSearch}
                  onChange={(event) => setAddProductSearch(event.target.value)}
                  placeholder="Search products..."
                />
              </div>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Product *</span>
                <select
                  name="product"
                  value={addProductId}
                  onChange={(event) => handleAddProductChange(event.target.value)}
                  required
                  disabled={isAddingProductPrice}
                  style={inputBaseStyle}
                >
                  <option value="">Select a product</option>
                  {filteredProductsToLink.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>

              {filteredProductsToLink.length === 0 ? (
                <p style={emptyStateStyle}>No products match your search.</p>
              ) : null}
            </>
          )}
        </FormModal>
      ) : null}

      {isAdmin ? (
        <Modal
          isOpen={isManageProductsModalOpen}
          onClose={() => setIsManageProductsModalOpen(false)}
          title="Manage Products"
          description="Review linked products and manage pricing from one place."
          hideFooter
          maxWidth={920}
          contentStyle={historyModalContentStyle}
        >
          {productPricingRows.length === 0 ? (
            <p style={emptyStateStyle}>No linked products for this supplier.</p>
          ) : (
            <div className="table-container supplier-detail-table-container" style={tableWrapStyle}>
              <table className="data-table supplier-pricing-table" style={{ ...tableStyle, minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Product</th>
                    <th style={tableHeadCellStyle}>Pricing Type</th>
                    <th style={tableHeadCellStyle}>Latest Price</th>
                    <th style={tableHeadCellStyle}>Last Update</th>
                    <th style={tableHeadCellStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productPricingRows.map((row) => {
                    const selectedVariantName = selectedVariantByProductId[row.productId];
                    const activeVariant =
                      row.variants.find((variant) => getVariantName(variant) === selectedVariantName) ||
                      row.variants[0] ||
                      null;

                    return (
                      <tr key={row.id} className="product-row">
                        <td style={tableBodyCellStyle}>{row.productName}</td>
                        <td style={tableBodyCellStyle}>{row.pricingMethodLabel}</td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct
                            ? formatPriceWithPricingMethod(
                              formatPrice(activeVariant?.currentPrice),
                              row.pricingMethod
                            )
                            : row.simplePrice}
                        </td>
                        <td style={tableBodyCellStyle}>
                          {row.isVariantProduct
                            ? formatDate(activeVariant?.lastUpdated)
                            : formatDate(row.simpleLastUpdate)}
                        </td>
                        <td style={tableBodyCellStyle}>
                          <div className="row-actions" style={rowActionsStyle}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() =>
                                row.isVariantProduct
                                  ? activeVariant
                                    ? handleOpenAddPriceModal(activeVariant, row.productName)
                                    : handleOpenAddVariantModal(row.productId, row.productName)
                                  : handleOpenAddSimplePriceModal(row)
                              }
                            >
                              Manage Pricing
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() =>
                                row.isVariantProduct && activeVariant
                                  ? handleOpenHistoryModal(activeVariant, row.productName)
                                  : handleOpenSimpleHistoryModal(row)
                              }
                              disabled={row.isVariantProduct && !activeVariant}
                            >
                              View History
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              style={{ padding: '8px 12px', fontSize: 13 }}
                              onClick={() => handleOpenRemoveProductLinkModal(row)}
                              disabled={isRemovingLink}
                            >
                              Remove Product Link
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      ) : null}

      {isAdmin ? (
        <FormModal
          isOpen={isVariantModalOpen}
          onClose={handleCloseVariantModal}
          onSubmit={handleSubmitVariant}
          title={`${variantModalMode === 'edit' ? 'Edit' : 'Add'} Variant - ${selectedProductName || 'Unknown product'}`}
          submitLabel={isSavingVariant ? 'Saving...' : variantModalMode === 'edit' ? 'Save Variant' : 'Add Variant'}
          submitDisabled={
            isSavingVariant ||
            (variantModalMode === 'add' && variantSelectOptions.length === 0)
          }
          cancelDisabled={isSavingVariant}
          errorMessage={supplierPriceError}
        >
          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Variant Name *</span>
            <select
              name="variantName"
              value={variantName}
              onChange={(event) => {
                setVariantName(event.target.value);
                if (supplierPriceError) {
                  setSupplierPriceError('');
                }
              }}
              required
              disabled={isSavingVariant || variantSelectOptions.length === 0}
              style={inputBaseStyle}
            >
              <option value="">Select a variant</option>
              {variantSelectOptions.map((optionName) => (
                <option key={optionName} value={optionName}>
                  {optionName}
                </option>
              ))}
            </select>
            {variantModalMode === 'add' && variantSelectOptions.length === 0 ? (
              <span style={emptyStateStyle}>
                No allowed variants are available for this supplier.
              </span>
            ) : null}
          </label>

          {variantModalMode === 'add' ? (
            <>
              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Price *</span>
                <input
                  name="initialPrice"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={variantInitialPrice}
                  onChange={(event) => {
                    setVariantInitialPrice(event.target.value);
                    if (supplierPriceError) {
                      setSupplierPriceError('');
                    }
                  }}
                  required
                  disabled={isSavingVariant}
                  style={inputBaseStyle}
                />
              </label>

              <label style={fieldWrapStyle}>
                <span style={labelStyle}>Pricing Type</span>
                <select
                  name="variantInitialPricingMethod"
                  value={variantInitialPricingMethod}
                  onChange={(event) => {
                    setVariantInitialPricingMethod(
                      normalizePricingMethod(event.target.value)
                    );
                    if (supplierPriceError) {
                      setSupplierPriceError('');
                    }
                  }}
                  disabled={isSavingVariant}
                  style={inputBaseStyle}
                >
                  {PRICING_METHOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Notes</span>
            <textarea
              name="variantNotes"
              value={variantNote}
              onChange={(event) => setVariantNote(event.target.value)}
              rows={3}
              disabled={isSavingVariant}
              style={{
                ...inputBaseStyle,
                resize: 'vertical',
              }}
            />
          </label>

          <div className="variant-specs-fieldset">
            <div className="variant-specs-fieldset__header">
              <span style={labelStyle}>Specs</span>
              <button
                type="button"
                className="button"
                onClick={handleAddSpecField}
                disabled={isSavingVariant}
              >
                Add field
              </button>
            </div>

            <div className="variant-specs-rows">
              {variantSpecs.map((spec, index) => (
                <div className="variant-spec-row" key={`spec-${index}`}>
                  <input
                    type="text"
                    value={spec.key}
                    placeholder="e.g. pitch, type, size"
                    aria-label={`Spec ${index + 1} key`}
                    onChange={(event) => handleSpecChange(index, 'key', event.target.value)}
                    disabled={isSavingVariant}
                    style={inputBaseStyle}
                  />
                  <input
                    type="text"
                    value={spec.value}
                    placeholder="e.g. 3.2, Indoor"
                    aria-label={`Spec ${index + 1} value`}
                    onChange={(event) => handleSpecChange(index, 'value', event.target.value)}
                    disabled={isSavingVariant}
                    style={inputBaseStyle}
                  />
                  <button
                    type="button"
                    className="button"
                    onClick={() => handleRemoveSpecField(index)}
                    disabled={isSavingVariant}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </FormModal>
      ) : null}

      {isAdmin ? (
        <FormModal
          isOpen={isPriceModalOpen}
          onClose={handleClosePriceModal}
          onSubmit={handleSubmitProductPrice}
          title={`Manage Pricing - ${selectedProductName || 'Unknown product'}`}
          submitLabel={isSubmittingPrice ? 'Saving...' : 'Save Price'}
          submitDisabled={isSubmittingPrice}
          cancelDisabled={isSubmittingPrice}
          errorMessage={supplierPriceError}
        >
          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Price *</span>
            <input
              name="price"
              type="number"
              min="0.01"
              step="0.01"
              value={price}
              onChange={(event) => {
                setPrice(event.target.value);
                if (supplierPriceError) {
                  setSupplierPriceError('');
                }
              }}
              required
              disabled={isSubmittingPrice}
              style={inputBaseStyle}
            />
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Pricing Type</span>
            <select
              name="pricingMethod"
              value={pricingMethod}
              onChange={(event) => {
                setPricingMethod(normalizePricingMethod(event.target.value));
                if (supplierPriceError) {
                  setSupplierPriceError('');
                }
              }}
              disabled={isSubmittingPrice}
              style={inputBaseStyle}
            >
              {PRICING_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Note</span>
            <textarea
              name="notes"
              value={priceNote}
              onChange={(event) => setPriceNote(event.target.value)}
              rows={4}
              disabled={isSubmittingPrice}
              style={{
                ...inputBaseStyle,
                resize: 'vertical',
              }}
            />
          </label>
        </FormModal>
      ) : null}

      <Modal
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        title={`Price History - ${selectedProductName || 'Unknown product'}`}
        hideFooter
        maxWidth={760}
        contentStyle={historyModalContentStyle}
      >
        {isHistoryLoading ? (
          <p style={emptyStateStyle}>Loading price history...</p>
        ) : selectedHistory.length === 0 ? (
          <p style={emptyStateStyle}>No price history available</p>
        ) : (
          <div className="table-container supplier-detail-table-container" style={tableWrapStyle}>
            <table className="data-table supplier-history-table" style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeadCellStyle}>Price</th>
                  <th style={tableHeadCellStyle}>Date</th>
                  <th style={tableHeadCellStyle}>Note</th>
                </tr>
              </thead>
              <tbody>
                {selectedHistory.map((entry, index) => (
                  <tr
                    key={`${selectedProductName}-${entry?.date?.seconds || entry?.date || 'missing-date'}-${entry?.price || 'missing-price'}-${index}`}
                  >
                    <td style={tableBodyCellStyle}>
                      {formatPriceWithPricingMethod(
                        formatPrice(entry?.price),
                        entry?.pricingMethod
                      )}
                    </td>
                    <td style={tableBodyCellStyle}>{formatDate(entry?.date)}</td>
                    <td style={tableBodyCellStyle}>{normalizeHistoryNote(entry?.note)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isRemoveModalOpen}
        onClose={handleCloseRemoveModal}
        onConfirm={handleConfirmRemoveLink}
        title={removeTargetType === 'variant' ? 'Remove Variant' : 'Remove Product Link'}
        description={
          removeTargetType === 'variant'
            ? `Are you sure you want to remove ${selectedProductName || 'this variant'}?`
            : `Are you sure you want to remove ${selectedProductName || 'this product'} and all its pricing history from this supplier?`
        }
        confirmLabel={isRemovingLink ? 'Removing...' : 'Remove'}
        confirmDisabled={isRemovingLink}
        cancelDisabled={isRemovingLink}
      />

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteModalOpen(false);
          }
        }}
        onConfirm={handleDelete}
        title="Delete Supplier"
        description="Are you sure you want to delete this supplier? You can restore it later from Trash."
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={isDeleting}
        cancelDisabled={isDeleting}
      />
    </div>
  );
}
